import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useAppSelector } from '../../redux/hooks.ts'
import { selectThemeStyle } from '../../features/setting/setting.slice.ts'
import IdeyaTypography from '../../common/antd/ideya-typography.tsx'
import { SyncOutlined } from '@ant-design/icons'
import { convertPxToNumber, isV2Ready } from '../../utils/helpers.ts'
import { IdeaListUserControls } from './components/idea-list-user-controls.tsx'
import {
  IdeaListTable,
  IdeaListTableRef,
} from './components/idea-list-table/idea-list-table.tsx'
import { CreateIdeaFormFieldTypes } from './components/idea-modal/idea-action/action-idea-form.tsx'
import { ActionIdeaModal } from './components/idea-modal/idea-action/action-idea-modal.tsx'
import { IdeaHistoryModal } from './components/idea-modal/idea-history/idea-history-modal.tsx'
import Alert from 'antd/es/alert/Alert'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import Form from 'antd/es/form/index'
import { SummaryInfo } from './components/idea-summary-info/idea-list-summary-info.tsx'
import {
  useGetBuyingPowerQuery,
  useGetFullyPnlQuery,
  useGetNetExposureQuery,
  useLazyGetBookSnapshotQuery,
} from '../../apis/statistics/statistics-api.api.ts'
import { selectIdeaListInput } from '../../redux/slices/idea-list-input.slice.ts'
import { selectUser } from '../../features/user/user.slice.ts'
import { useIsMobile } from '../../common/hooks/use-is-mobile.ts'
import { useTranslation } from 'react-i18next'
import { IdeaActionLogModal } from './components/idea-modal/idea-action-log/idea-action-log-modal.tsx'
import { getCurrentQuarterRange } from '../../utils/time.ts'
import dayjs from 'dayjs'
import { useTableHeight } from '../../common/hooks/use-table-height.ts'

const MemoizedIdeaListTable = memo(IdeaListTable)

export const IdeaList = () => {
  const [form] = Form.useForm<CreateIdeaFormFieldTypes>()
  const [showSummaryInfoSection, setShowSummaryInfoSection] =
    useState<boolean>(true)
  const [lastUpdatedTime, setLastUpdatedTime] = useState<Date>(new Date())
  const [showActionIdeaModal, setShowActionIdeaModal] = useState<boolean>(false)
  const [showIdeaHistoryModal, setShowIdeaHistoryModal] =
    useState<boolean>(false)
  const [showActionLogModal, setShowActionLogModal] = useState<boolean>(false)
  const [showLiquidateIdeaModal, setShowLiquidateIdeaModal] =
    useState<boolean>(false)
  const [actionIdeaId, setActionIdeaId] = useState<string | null>(null) // <-- add this state
  const [historyIdeaId, setHistoryIdeaId] = useState<string | null>(null) // <-- add this state
  const themeStyle = useAppSelector(selectThemeStyle)
  const tableRef = useRef<IdeaListTableRef>(null)
  const ideaListInput = useAppSelector(selectIdeaListInput)
  const userInfo = useAppSelector(selectUser)
  const isMobile = useIsMobile(768)
  const { t } = useTranslation()
  const headerRef = useRef<HTMLDivElement>(null)
  const tableHeight = useTableHeight(headerRef)

  const {
    data: buyingPowerData,
    refetch: refetchBuyingPower,
    isLoading: isBuyingPowerLoading,
    isFetching: isBuyingPowerFetching,
  } = useGetBuyingPowerQuery(
    {
      bookId:
        ideaListInput.contributor === ''
          ? userInfo.bookId
          : ideaListInput.bookId,
    },
    { skip: !userInfo.bookId && !ideaListInput.bookId }
  )

  const {
    data: netExposureData,
    refetch: refetchNetExposure,
    isLoading: isNetExposureLoading,
    isFetching: isNetExposureFetching,
  } = useGetNetExposureQuery(
    {
      bookId:
        ideaListInput.contributor === ''
          ? userInfo.bookId
          : ideaListInput.bookId,
    },
    { skip: !userInfo.bookId && !ideaListInput.bookId }
  )

  const {
    data: fullyPnlData,
    refetch: refetchFullyPnl,
    isLoading: isFullyPnlLoading,
    isFetching: isFullyPnlFetching,
  } = useGetFullyPnlQuery(
    {
      bookId:
        ideaListInput.contributor === ''
          ? userInfo.bookId
          : ideaListInput.bookId,
    },
    { skip: !userInfo.bookId && !ideaListInput.bookId }
  )

  const [
    getSnapshotData,
    { currentData: snapshotData, isLoading: isSnapshotLoading },
  ] = useLazyGetBookSnapshotQuery()

  // Memoized callback to update last updated time
  const handleTableRefreshed = useCallback(() => {
    setLastUpdatedTime(new Date())
  }, [])

  const handleCloseActionIdeaModal = () => {
    form.resetFields()
    setActionIdeaId(null)
    setShowActionIdeaModal(false)
    tableRef.current?.refresh()
  }

  useEffect(() => {
    const handler = () => {
      tableRef.current?.refresh()
    }
    window.addEventListener('refresh-idea-list', handler)
    return () => window.removeEventListener('refresh-idea-list', handler)
  }, [])

  useEffect(() => {
    if (ideaListInput.dateRange !== getCurrentQuarterRange()) {
      // Internal admins should not call getSnapshotData with empty bookId
      if (userInfo.role !== '') {
        if (ideaListInput.bookId) {
          getSnapshotData({
            bookId: ideaListInput.bookId,
            date: dayjs(ideaListInput.dateRange[1]).format('YYYY-MM-DD') || '',
          })
        }
      } else {
        getSnapshotData({
          bookId:
            ideaListInput.contributor === ''
              ? userInfo.bookId
              : ideaListInput.bookId,
          date: dayjs(ideaListInput.dateRange[1]).format('YYYY-MM-DD') || '',
        })
      }
    }
  }, [ideaListInput.bookId, ideaListInput.dateRange, userInfo.bookId])

  const endDate = dayjs(ideaListInput.dateRange[1]).format('YYYY-MM-DD')
  const isQuarterEnd = endDate === getCurrentQuarterRange()[1]
  const isFutureOrToday =
    dayjs(endDate).isSame(dayjs(), 'day') ||
    dayjs(endDate).isAfter(dayjs(), 'day')
  const isHistorical = !isQuarterEnd && !isFutureOrToday

  return (
    <>
      <div ref={headerRef}>
        <div style={{ marginBottom: themeStyle.marginLG }}>
          <SummaryInfo
            useSnapshotData={isHistorical}
            snapshotData={snapshotData?.data}
            buyingPower={buyingPowerData}
            netExposure={netExposureData?.data}
            pnlData={fullyPnlData?.data}
            showSummaryInfoSection={showSummaryInfoSection}
            setShowSummaryInfoSection={() =>
              setShowSummaryInfoSection(!showSummaryInfoSection)
            }
            isBuyingPowerLoading={
              isBuyingPowerLoading || isBuyingPowerFetching || isSnapshotLoading
            }
            isNetExposureLoading={
              isNetExposureLoading || isNetExposureFetching || isSnapshotLoading
            }
            isFullyPnlLoading={isFullyPnlLoading || isFullyPnlFetching}
          />
        </div>
        <Row
          justify={'space-between'}
          style={{
            display: 'flex',
            borderRadius: '8px',
            border: themeStyle.myTitleBorder,
            padding: '10px 16px',
            background: themeStyle.myTitleBg,
            marginBottom: themeStyle.marginXS,
          }}
        >
          <Col>
            <IdeyaTypography.Text size={'base'}>
              {t('idea_list.idea_list')}
            </IdeyaTypography.Text>
          </Col>
          <Col>
            <Row
              gutter={[
                convertPxToNumber(themeStyle.marginXXS),
                convertPxToNumber(themeStyle.marginXXS),
              ]}
            >
              <Col>
                {isMobile ? (
                  <IdeyaTypography.Text size={'base'}>
                    {lastUpdatedTime.toLocaleString('sv-SE', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: false,
                    })}
                  </IdeyaTypography.Text>
                ) : (
                  <IdeyaTypography.Text size={'base'}>
                    {t('idea_list.latest_updated_time')} :{' '}
                    {lastUpdatedTime.toLocaleString()}
                  </IdeyaTypography.Text>
                )}
              </Col>
              <Col>
                <SyncOutlined
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    tableRef.current?.refresh()
                    setLastUpdatedTime(new Date())
                    if (userInfo.bookId || ideaListInput.bookId) {
                      refetchBuyingPower()
                      refetchNetExposure()
                      refetchFullyPnl()
                    }
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        {isV2Ready && (
          <Alert
            type={'success'}
            closable
            showIcon
            message={'Net Exposure Breach: 12%'}
          />
        )}
        <IdeaListUserControls
          onCreateIdea={() => setShowActionIdeaModal(true)}
        />
      </div>
      <MemoizedIdeaListTable
        ref={tableRef}
        onRefreshed={handleTableRefreshed}
        onEditIdea={useCallback((id: string) => {
          setActionIdeaId(id)
          setShowActionIdeaModal(true)
        }, [])}
        onViewIdea={useCallback((id: string) => {
          setHistoryIdeaId(id)
          setShowActionLogModal(true)
        }, [])}
        onViewHistoryIdea={useCallback((id: string) => {
          setHistoryIdeaId(id)
          setShowIdeaHistoryModal(true)
        }, [])}
        onLiquidateIdea={useCallback((id: string) => {
          setActionIdeaId(id)
          setShowActionIdeaModal(true)
          setShowLiquidateIdeaModal(true)
        }, [])}
        height={tableHeight}
      />
      <ActionIdeaModal
        showActionModal={showActionIdeaModal}
        showLiquidateModal={showLiquidateIdeaModal}
        setShowLiquidatedModal={setShowLiquidateIdeaModal}
        form={form}
        handleCloseModal={handleCloseActionIdeaModal}
        onIdeaActioned={() => {
          tableRef.current?.refresh()
          refetchBuyingPower()
          refetchNetExposure()
          refetchFullyPnl()
        }}
        editIdeaId={actionIdeaId}
        setEditIdeaId={setActionIdeaId}
      />
      <IdeaHistoryModal
        showHistoryModal={showIdeaHistoryModal}
        ideaId={historyIdeaId}
        handleCloseModal={() => {
          setHistoryIdeaId(null)
          setShowIdeaHistoryModal(false)
        }}
      />
      <IdeaActionLogModal
        showActionLogModal={showActionLogModal}
        ideaId={historyIdeaId}
        handleCloseModal={() => setShowActionLogModal(false)}
      />
    </>
  )
}
